import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import ModalBase from "./ModalBase";

import { usePostHogCapture } from "../../hooks/usePostHogCapture";

import {
  AssetTier,
  ASSET_LIMIT_BY_TIER,
  MODEL_LIMIT_BY_TIER,
  PLAN_LABEL_BY_TIER,
} from "../../api/stripeSubscription";
import { POSTHOG_EVENTS } from "../../utils/posthogEvents";

// Which capped resource hit its tier limit — drives copy, fallback limit, and analytics.
export type PlanLimitItem = "asset" | "model";

interface PlanLimitModalProps {
  itemLabel?: PlanLimitItem;
  tier?: AssetTier;
  limit?: number;
  currentCount?: number;
  /** Which creation flow hit the cap, e.g. "quick_setup" or "chat_proposal". */
  source?: string;
  onClose?: (result?: any) => void;
}

const ITEM_COPY: Record<PlanLimitItem, { singular: string; plural: string; verb: string }> = {
  asset: { singular: "asset", plural: "assets", verb: "add" },
  model: { singular: "model", plural: "models", verb: "deploy" },
};

const ITEM_EVENTS: Record<PlanLimitItem, { reached: string; upgradeClicked: string }> = {
  asset: {
    reached: POSTHOG_EVENTS.ASSET_LIMIT_REACHED,
    upgradeClicked: POSTHOG_EVENTS.ASSET_LIMIT_UPGRADE_CLICKED,
  },
  model: {
    reached: POSTHOG_EVENTS.MODEL_LIMIT_REACHED,
    upgradeClicked: POSTHOG_EVENTS.MODEL_LIMIT_UPGRADE_CLICKED,
  },
};

const PlanLimitModal: React.FC<PlanLimitModalProps> = ({
  itemLabel = "asset",
  tier = "no_subscription",
  limit,
  currentCount,
  source,
  onClose,
}) => {
  const navigate = useNavigate();
  const { capture } = usePostHogCapture();

  const item = ITEM_COPY[itemLabel] ?? ITEM_COPY.asset;
  const events = ITEM_EVENTS[itemLabel] ?? ITEM_EVENTS.asset;
  // Falls back to the no-subscription cap so a missing prop never renders "up to undefined"; callers always pass it via the check helpers.
  const fallbackLimit =
    itemLabel === "model" ? MODEL_LIMIT_BY_TIER.no_subscription : ASSET_LIMIT_BY_TIER.no_subscription;
  const effectiveLimit = typeof limit === "number" ? limit : fallbackLimit;
  // A zero cap isn't a limit the user has filled — it's a plan they don't have yet, so the copy sells the plan instead.
  const requiresPaidPlan = effectiveLimit <= 0;

  // Fire once when the limit is surfaced to the user.
  useEffect(() => {
    capture(events.reached, {
      tier,
      limit: effectiveLimit,
      current_count: currentCount,
      source,
      item: itemLabel,
    });
    // Only on mount — the modal is created fresh each time the cap is hit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpgrade = () => {
    capture(events.upgradeClicked, { tier, limit: effectiveLimit, source, item: itemLabel });
    onClose?.();
    navigate("/home/subscriptions");
  };

  const planLabel = PLAN_LABEL_BY_TIER[tier] ?? "current";
  const noun = effectiveLimit === 1 ? item.singular : item.plural;

  let description: string;
  if (requiresPaidPlan) {
    description = `${item.plural.charAt(0).toUpperCase()}${item.plural.slice(1)} can only be ${
      itemLabel === "model" ? "deployed" : "added"
    } on a paid plan. Subscribe to Essential or Pro to ${item.verb} ${item.plural}.`;
  } else if (tier === "no_subscription") {
    description = `You can have up to ${effectiveLimit} ${noun} without a subscription. Subscribe to a paid plan to ${item.verb} more.`;
  } else {
    description = `Your ${planLabel} plan includes up to ${effectiveLimit} ${noun}. Upgrade your plan to ${item.verb} more.`;
  }

  return (
    <ModalBase
      title={requiresPaidPlan ? "Paid plan required" : `${itemLabel === "model" ? "Model" : "Asset"} limit reached`}
      onClose={() => onClose?.()}
      size="xs"
      footer={
        <>
          <button className="eai-btn-outline flex-1" onClick={() => onClose?.()}>
            Cancel
          </button>
          <button className="eai-btn-cta flex-1" onClick={handleUpgrade}>
            {requiresPaidPlan ? "View Plans" : "Upgrade Plan"}
          </button>
        </>
      }
    >
      <div className="eai-description-sm flex flex-col gap-2">
        <p>{description}</p>
        {!requiresPaidPlan && typeof currentCount === "number" && (
          <p className="text-eai-ink-faint">
            You currently have {currentCount} of {effectiveLimit} {item.plural}.
          </p>
        )}
      </div>
    </ModalBase>
  );
};

export default PlanLimitModal;
